// SupplierProductDetail Page - Single supplier product detail
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  MapPin,
  Package,
  Star,
  AlertCircle,
  Truck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Heart,
} from 'lucide-react'
import clsx from 'clsx'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import {
  BackorderBadge,
  Button,
  InStockBadge,
  QuantitySelector,
  useToast,
  VanStockBadge,
} from '../../components/radix'
import { useCart } from '../../auth/hooks/useCart'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from '../../components/engineer/favourites/useFavouriteToggle'
import { usePostcodeSync } from '../../hooks/usePostcodeSync'
import { HOME_TAB_ENABLED } from '../../config/features'
import { api } from '../../services/api'
import {
  ProductViewType,
  clearProductViewSource,
  resolveProductViewSource,
  trackProductViewed,
  trackStandardProductAdd,
} from '../../lib/analytics'
import { formatBackorderAvailability, formatEarliestDate } from '../../utils/datetime'
import type { FulfillmentOption } from '../../types'
import { perUnitPrice } from '../../utils/pricing'
import { toastClearanceRef } from '../../utils/heightVarRef'
import {
  removeItemFulfillmentMethod,
  writeCartFulfillmentMethod,
  writeItemFulfillmentMethod,
} from '../../utils/cartFulfillment'
import { PRODUCT_ADD_MODES, QUICK_ADD_METHOD_PARAM } from '../../constants/quickAdd'

type FulfillmentMethod = 'pickup' | 'delivery'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

export function SupplierProductDetail() {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenantSlug, productId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  // Get branchId from URL query params - guard against literal "null" string
  const rawBranchId = searchParams.get('branchId')
  const branchIdFromUrl = rawBranchId && rawBranchId !== 'null' && rawBranchId !== 'undefined' ? rawBranchId : null
  // Collection / delivery chosen on a linked search card (QA-001 AC6). Only
  // that link sets it; opened any other way, the page picks as before.
  const rawMethodFromSearchCard = searchParams.get(QUICK_ADD_METHOD_PARAM)
  const methodFromSearchCard: FulfillmentMethod | null =
    rawMethodFromSearchCard === 'pickup' || rawMethodFromSearchCard === 'delivery'
      ? rawMethodFromSearchCard
      : null
  const { addToast } = useToast()
  const { addToCart: addToCartMutation, cart, updateCartLine, isAdding, isUpdating } = useCart()
  const [isBranchSelectorOpen, setIsBranchSelectorOpen] = useState(false)
  const [isUpdatingCart, setIsUpdatingCart] = useState(false)
  // Which fulfilment method the engineer picked via the radio-select tiles.
  // Seeded to pickup; auto-flips to delivery below when the branch can't collect.
  const [selectedMethod, setSelectedMethod] = useState<FulfillmentMethod>(
    methodFromSearchCard ?? 'pickup',
  )
  // Selected branch drives the stock / branch-info PREVIEW. Defaults to the
  // branch we arrived with (the cart line's branch). The cart line only moves
  // to this branch when the engineer hits Update Cart (see handleAddToCart).
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(branchIdFromUrl)
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()

  const [quantity, setQuantity] = useState<number | null>(1)
  const [session, setSession] = useState<EngineerSession | null>(null)
  const [showDescription, setShowDescription] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  // Order-on-Demand: quantity-specific DC collection/delivery dates.
  const [dcCollectionDate, setDcCollectionDate] = useState<string | null>(null)
  const [dcDeliveryDate, setDcDeliveryDate] = useState<string | null>(null)
  const [isCheckingDcDate, setIsCheckingDcDate] = useState(false)
  // Snapshot of `quantity` that only advances when the debounce timer fires.
  // The fulfilment tile ("N from DC" + DC date) reads this instead of live
  // `quantity`, so the number never jumps ahead of the date it belongs to.
  // Live `quantity` still drives the stepper input, Add-to-Cart, and pricing.
  const [settledQuantity, setSettledQuantity] = useState<number | null>(1)
  // Analytics: report product_viewed once per opened raw product.
  const viewedProductRef = useRef<string | null>(null)
  const dcCheckSeqRef = useRef(0)
  const lastCheckedQtyRef = useRef<number | null>(null)

  // Load session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    } else {
      navigate(`/${tenantSlug}/engineer`)
    }
  }, [navigate, tenantSlug])

  // Check if postcode changed on another device (cross-device sync)
  usePostcodeSync(session)

  // Keep the selected (preview) branch aligned with the URL branch — after a
  // committed Update Cart moves the line, or when navigating between items.
  useEffect(() => {
    setSelectedBranchId(branchIdFromUrl)
  }, [branchIdFromUrl])

  // Set initial image to thumbnail when product loads
  useEffect(() => {
    if (!product) return

    if (product.images && product.images.length > 0) {
      // Find thumbnail image index
      const thumbnailIndex = product.images.findIndex((img: any) => img.is_thumbnail)
      // Set to thumbnail if found, otherwise default to 0
      setCurrentImageIndex(thumbnailIndex >= 0 ? thumbnailIndex : 0)
    } else {
      setCurrentImageIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  // Fetch supplier product
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['supplier-product', productId],
    queryFn: () => api.getSupplierProduct(productId!),
    enabled: !!session && !!productId,
  })

  // Analytics: raw-product view, once loaded and once per id. Full supplier
  // detail is known here because this page IS one supplier's listing.
  useEffect(() => {
    if (!product || !productId) return
    if (viewedProductRef.current === productId) return
    viewedProductRef.current = productId
    trackProductViewed({
      type: ProductViewType.RAW,
      rawProductId: String(product.productId),
      rawProductName: product.productName,
      masterProductId: product.masterProductId
        ? String(product.masterProductId)
        : null,
      masterProductName: product.masterProductName ?? null,
      supplier: product.supplierName || null,
      supplierId:
        product.supplierId != null ? String(product.supplierId) : null,
      source: resolveProductViewSource(location.state),
    })
    // A reload restores this source from history; a reload is a direct visit,
    // so forget it now that the view is recorded.
    clearProductViewSource()
  }, [product, productId, location.state])

  // Fetch live price for this single product
  const { data: livePriceData } = useQuery({
    queryKey: ['live-price', productId],
    queryFn: () => api.getLivePrices([Number(productId)]),
    enabled: !!product && !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // Fetch live stock for this product
  // IMPORTANT: If branchId in URL, use cart-stock endpoint (exact branch)
  //            Otherwise, use live-stock endpoint (nearest 5 branches)
  const { data: liveStockData, isLoading: isLoadingStock, isError: isStockError } = useQuery({
    queryKey: selectedBranchId
      ? ['cart-stock', productId, selectedBranchId, session?.postcode]
      : ['live-stock', productId, session?.postcode, session?.latitude, session?.longitude],
    queryFn: () => {
      if (selectedBranchId) {
        // Specific (selected) branch - use cart-stock endpoint
        return api.getCartStock(
          [{ raw_product_id: productId!, branch_id: selectedBranchId }],
          session!.postcode,
          session!.latitude,
          session!.longitude
        )
      } else {
        // No branch specified - use live-stock endpoint (nearest 5)
        return api.getLiveStock(
          [productId!],
          session!.postcode,
          session!.latitude,
          session!.longitude
        )
      }
    },
    enabled: !!product && !!productId && !!session?.postcode,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2, // Retry failed requests twice
  })

  // Fetch the SELECTED branch's info so the branch detail section previews it
  // (name/address/phone), even when its stock is unavailable.
  const { data: branchData } = useQuery({
    queryKey: ['branch', selectedBranchId],
    queryFn: () => api.getBranch(selectedBranchId!),
    enabled: !!selectedBranchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Nearby branches (with stock) that the engineer can switch this line to.
  // live-stock returns the nearest branches for the product; the selector lists
  // them so the engineer can preview and switch branches (see the selector).
  const { data: branchOptionsData } = useQuery({
    queryKey: ['branch-options', productId, session?.postcode, session?.latitude, session?.longitude],
    queryFn: () => api.getLiveStock(
      [productId!],
      session!.postcode,
      session!.latitude,
      session!.longitude,
    ),
    enabled: !!product && !!productId && !!session?.postcode,
    staleTime: 2 * 60 * 1000,
  })

  // Get live price if available (match by product ID)
  const livePrice = livePriceData?.prices?.find(
    p => String(p.raw_product_id) === String(productId)
  )
  // Check if price is unavailable (API failed or returned null)
  const priceUnavailable = livePrice?.unavailable === true
  // Safe price parsing with fallback
  const displayPrice = (() => {
    if (priceUnavailable) {
      // Fall back to product price when live price unavailable
      if (product?.price) {
        const parsed = typeof product.price === 'number' ? product.price : parseFloat(String(product.price))
        if (!isNaN(parsed)) return parsed
      }
      return 0
    }
    if (livePrice && livePrice.selling_price) {
      const parsed = parseFloat(livePrice.selling_price)
      if (!isNaN(parsed)) return parsed
    }
    // Fall back to product price
    if (product?.price) {
      const parsed = typeof product.price === 'number' ? product.price : parseFloat(String(product.price))
      if (!isNaN(parsed)) return parsed
    }
    return 0
  })()

  // Get live stock. Prefer branchOptionsData for the selected branch when it's
  // in the nearest set — that response was fetched once at mount and holds all
  // nearby branches' stock, so switching branches becomes an instant local swap
  // instead of a cart-stock refetch flash. Mirrors master's SupplierListingCard,
  // which switches `activeListing` locally against its preloaded `branches`
  // prop without firing another network call. cart-stock is only used as a
  // fallback for a URL-supplied branchId that isn't in the nearest set.
  const stockItem = (() => {
    if (selectedBranchId && branchOptionsData?.stock) {
      const fromOptions = branchOptionsData.stock.find(
        (s: any) =>
          String(s.raw_product_id) === String(productId) &&
          String(s.branch_id) === String(selectedBranchId),
      )
      if (fromOptions) return fromOptions
    }
    return liveStockData?.stock?.find(s => {
      const productMatches = String(s.raw_product_id) === String(productId)
      if (selectedBranchId) {
        return productMatches && String(s.branch_id) === String(selectedBranchId)
      }
      // Otherwise, return first match (nearest branch, sorted by distance).
      return productMatches
    })
  })()
  const collectionStock = stockItem?.collection_stock ?? stockItem?.stock_quantity ?? product?.stockQuantity ?? 0
  const deliveryStock = stockItem?.delivery_stock ?? stockItem?.stock_quantity ?? product?.stockQuantity ?? 0

  // Check if stock is unavailable (API returned empty array or no matching stock)
  const stockUnavailable = !isLoadingStock && !stockItem &&
    (isStockError || (liveStockData && (!liveStockData.stock || liveStockData.stock.length === 0)))
  // Only show the skeleton state when we DON'T have a stockItem yet — a
  // branch swap that resolves against the already-fetched branchOptionsData
  // must not flash a skeleton just because cart-stock is refetching in the
  // background. This is what makes the raw page's branch swap feel instant
  // (matches master).
  const showLoadingSkeleton = isLoadingStock && !stockItem
  // Branch details: prioritize live stock, fallback to branchData (when branchId in URL)
  const displayBranchName = stockItem?.branch_name || branchData?.name || ''
  const displayBranchId = stockItem?.branch_id || branchData?.id || null
  // Prefer the supplier's live UOM display when the stock response
  // carries one (currently Travis Perkins, resolved to standard format
  // on the backend). Falls back to the catalogue value otherwise, so
  // suppliers that don't surface UOM at stock time render unchanged.
  const displayUnitOfMeasure = (stockItem as any)?.uom ?? product?.unitOfMeasure

  // A product is limited to ONE branch in the cart at a time. Prefer a line at
  // the branch currently in view, else fall back to the same product at any
  // other branch. This keeps the button in "Update" mode (never a duplicate
  // "Add") and lets Update Cart MOVE the existing line to the viewed branch.
  // Preferring the viewed-branch line first also guarantees a move never
  // targets a branch that already holds the product, so no 409 is possible.
  const viewedBranchId = selectedBranchId ?? displayBranchId
  const existingCartItem =
    cart.find(item =>
      String(item.productId) === String(productId) &&
      String(item.branchId) === String(viewedBranchId)
    ) ??
    cart.find(item => String(item.productId) === String(productId))

  // Pre-fill quantity from cart if item exists; otherwise seed from the product's
  // multiple. Runs on mount / cart-line / product change only — NOT on live-price
  // load — so a slow live response never overwrites a quantity the engineer typed
  // or an in-progress edit to an existing line. A quantity left invalid by a
  // genuine live multiple change is corrected by the effect below, not here.
  useEffect(() => {
    if (existingCartItem) {
      setQuantity(existingCartItem.quantity)
    } else {
      setQuantity(product?.quantityMultiple ?? 1)
    }
  }, [existingCartItem, product?.quantityMultiple])

  // Seed the fulfilment method radio-tile from the existing cart line so the
  // page opens on whichever method was previously chosen — otherwise the
  // default 'pickup' would silently flip a delivery-only line back to pickup
  // on the next Update Cart tap.
  // The method picked on a linked search card wins (the card itself started
  // on the cart line's method unless the engineer changed it there).
  useEffect(() => {
    if (methodFromSearchCard) return
    if (existingCartItem?.fulfillmentMethod === 'delivery' || existingCartItem?.fulfillmentMethod === 'pickup') {
      setSelectedMethod(existingCartItem.fulfillmentMethod)
    }
  }, [existingCartItem?.id, existingCartItem?.fulfillmentMethod, methodFromSearchCard])

  // Selectable branches for the picker (nearest branches with per-branch stock).
  const branchOptions = (branchOptionsData?.stock ?? [])
    .filter((s: any) => String(s.raw_product_id) === String(productId) && s.branch_id)
    .map((s: any) => ({
      branchId: String(s.branch_id),
      branchName: s.branch_name || '',
      distanceMiles: s.distance_miles ?? null,
      pickupStock: s.collection_stock ?? s.stock_quantity ?? 0,
      deliveryStock: s.delivery_stock ?? 0,
      canCollect: s.can_collect !== false,
      canDeliver: s.can_deliver !== false,
    }))
  const hasOtherBranches = branchOptions.some(
    (b: { branchId: string }) => String(b.branchId) !== String(selectedBranchId),
  )

  // Select a branch to PREVIEW (stock/branch info re-point to it). No cart
  // write here — the cart line only moves to this branch on Update Cart.
  const handleSelectBranch = (newBranchId: string) => {
    setSelectedBranchId(String(newBranchId))
    setIsBranchSelectorOpen(false)
  }

  // ===== Order-on-Demand (OOD) availability =====
  // Mirror SupplierListingCard: when the supplier exposes DC stock, the engineer
  // can order up to the depot quantity; quantities above what's physically at the
  // branch get a DC-backed collection/delivery date via a live qty-specific
  // re-check.
  const branchNow = (stockItem as any)?.branch_stock ?? collectionStock
  const dcStock = (stockItem as any)?.dc_stock ?? null
  const canCollect = (stockItem as any)?.can_collect !== false
  const canDeliver = (stockItem as any)?.can_deliver !== false
  // Existing branch-stock cap (pickup priority, fall back to delivery).
  const availableStockBase = collectionStock > 0 ? collectionStock : deliveryStock
  // Phase 4: cap is computed at the view layer (branch + pickup_dc_stock).
  // Frontend just reads. Fall back to legacy availableStockBase if missing.
  const maxOrderQty = (stockItem as any)?.cap_total ?? availableStockBase
  // Backorder tier (backend-decided per supplier + product). Only trusted
  // when a live stock row exists — a failed/missing stock check is never a
  // backorder. Mirrors ProductDetail / SupplierListingCard.
  const backorderAllowed = !!stockItem && stockItem.backorder_allowed === true
  const backorderDateFromApi = stockItem?.backorder_date_from_api === true
  // Tagged with the qty they were fetched for so a stale quote is never
  // used for a different qty (mirrors the sheet's freshDcDates).
  const [freshBackorderDates, setFreshBackorderDates] = useState<
    { qty: number; collection: string | null; delivery: string | null } | null
  >(null)
  // Primary delivery follows delivery_stock. dcStock is reserved for the
  // later/DC tier so dc_stock: 0 does not erase branch-backed delivery.
  const deliveryQty = canDeliver ? deliveryStock : 0
  // Products sold in fixed multiples (packs / lengths). The selector steps by
  // this; a non-multiple quantity is blocked from being added to the cart.
  // CEF returns a fresh quantity_multiple with its live price; prefer it so the
  // per-unit price and multiple stay in lockstep. Other suppliers omit it, so
  // the cached catalogue value is used unchanged.
  const orderMultiple =
    livePrice?.quantity_multiple ?? product?.quantityMultiple ?? 1
  const isInvalidMultiple =
    orderMultiple > 1 && quantity !== null && quantity > 0 && quantity % orderMultiple !== 0

  // The requested qty needs a DC-backed date when DC stock is surfaced and the
  // qty sits above branch stock but within the cap.
  const needsDcDate =
    dcStock != null &&
    quantity !== null &&
    quantity > branchNow &&
    quantity <= maxOrderQty &&
    !isLoadingStock
  // Per-channel capacity (branch shelf + DC-later tier), gated by
  // canCollect/canDeliver. Used below to gate needsBackorderDate on the
  // SELECTED channel — otherwise a delivery line above delivery capacity but
  // below the union `maxOrderQty` (e.g. pickup cap 100, delivery cap 20,
  // qty 50, delivery selected) would never fire the qty-aware probe, and the
  // footer would advertise/submit a stale qty-1 backorder date. Mirrors
  // master SupplierListingCard's `selectedChannelCapacity`.
  const pickupBranchStockForGate = (stockItem as any)?.branch_stock ?? collectionStock
  const pickupDcStockForGate = (stockItem as any)?.pickup_dc_stock ?? 0
  const deliveryDcStockForGate = (stockItem as any)?.delivery_dc_stock ?? 0
  const pickupChannelCapacity = canCollect
    ? Math.max(pickupBranchStockForGate, pickupDcStockForGate)
    : 0
  const deliveryChannelCapacity = canDeliver
    ? Math.max(deliveryQty, deliveryDcStockForGate)
    : 0
  const selectedChannelCapacity =
    selectedMethod === 'delivery' ? deliveryChannelCapacity : pickupChannelCapacity
  // Backorder above the SELECTED channel's capacity for an API-date supplier
  // (its quoted date is qty-aware) — re-probe at the requested qty. Admin-date
  // suppliers don't.
  const needsBackorderDate =
    backorderAllowed &&
    backorderDateFromApi &&
    quantity !== null &&
    quantity > selectedChannelCapacity &&
    !isLoadingStock
  const needsQtyCheck = needsDcDate || needsBackorderDate

  // Trailing debounce + dedup + stale-guard: one live call per settled quantity.
  useEffect(() => {
    if (!needsQtyCheck || quantity === null) {
      setDcCollectionDate(null)
      setDcDeliveryDate(null)
      setFreshBackorderDates(null)
      setIsCheckingDcDate(false)
      lastCheckedQtyRef.current = null
      // Exiting the DC range — snap the tile to live so the DC tier disappears
      // immediately instead of hanging frozen on the old settled qty.
      setSettledQuantity(quantity)
      return
    }
    if (quantity === lastCheckedQtyRef.current) return

    // Trailing debounce: nothing visible changes until the user stops.
    // The "checking" flip and the live call both wait for the timer, so
    // rapid stepper taps or keystrokes reset the timer via the effect
    // cleanup and never flash the skeleton mid-press.
    const timer = setTimeout(() => {
      const seq = ++dcCheckSeqRef.current
      setIsCheckingDcDate(true)
      // Advance the tile-display qty in the same beat as the API call so the
      // "N from DC" number and the fetched date update together.
      setSettledQuantity(quantity)
      api
        .checkStock({
          supplier_id: Number(product?.supplierId),
          product_codes: [product?.supplierProductCode],
          branch_id: String(displayBranchId),
          postcode: session?.postcode,
          quantity,
          bypass_cache: true,
        })
        .then((response: any) => {
          if (seq !== dcCheckSeqRef.current) return
          const firstItem = response?.items?.[0]
          // Pickup/Delivery DC tiles want ONLY the explicit DC dates. No
          // fallback to earliest_collection / earliest_delivery (the
          // branch's own dates) — surfacing those as DC dates would
          // mislabel (e.g. "Today" instead of the real depot ETA).
          setDcCollectionDate(firstItem?.pickup_dc_date ?? null)
          setDcDeliveryDate(firstItem?.delivery_dc_date ?? null)
          // API-date backorder: the supplier's earliest dates at this qty.
          setFreshBackorderDates({
            qty: quantity,
            collection: firstItem?.earliest_collection ?? null,
            delivery: firstItem?.earliest_delivery ?? null,
          })
          lastCheckedQtyRef.current = quantity
        })
        .catch(() => {
          if (seq === dcCheckSeqRef.current) {
            setDcCollectionDate(null)
            setDcDeliveryDate(null)
            setFreshBackorderDates(null)
          }
        })
        .finally(() => {
          if (seq === dcCheckSeqRef.current) setIsCheckingDcDate(false)
        })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsQtyCheck,
    quantity,
    displayBranchId,
    product?.supplierId,
    product?.supplierProductCode,
    session?.postcode,
  ])

  const pickupAvailableNow = branchNow
  const pickupAvailableNowLabel = formatEarliestDate((stockItem as any)?.earliest_collection)
  // LATER quantity = depot excess OVER branch (display-only subtraction;
  // dc_stock in the schema stays raw). When dc==branch, this is 0 and the
  // ``> 0`` gate hides the redundant row.
  // Phase 3: adapter-supplied LATER qty. No fallback math.
  const pickupLaterQty = (stockItem as any)?.pickup_dc_stock ?? 0
  // Uses settledQuantity so the "N from DC" number stays aligned with the
  // last-fetched DC date (see settledQuantity above).
  const pickupOverflowQty = Math.max((settledQuantity ?? 0) - pickupAvailableNow, 0)
  // DC tier date — ONLY genuine DC sources. earliest_collection would be the
  // BRANCH'S collect-today date and must not be used as a DC fallback.
  // Phase 2: trust the adapter. pickup_dc_date is set by the backend only
  // when a real DC date applies. Frontend just reads it.
  const pickupDcDate = isCheckingDcDate
    ? null
    : (dcCollectionDate ?? (stockItem as any)?.pickup_dc_date ?? null)
  const pickupDcDateLabel = formatEarliestDate(pickupDcDate)
  const pickupLaterLabel = pickupDcDateLabel ?? '2-5 Business Days'
  // Hide the LATER row when its qty matches the primary tile's qty to
  // avoid a duplicate-looking row.
  const showPickupLaterTier =
    canCollect && dcStock != null && pickupLaterQty > 0 && pickupLaterQty !== pickupAvailableNow
  const showPickupDcTier =
    canCollect &&
    dcStock != null &&
    pickupOverflowQty > 0 &&
    (pickupDcDateLabel != null || isCheckingDcDate)
  const pickupLaterDisplayQty = Math.min(pickupOverflowQty, pickupLaterQty)
  // Phase 4: adapter now emits delivery_stock as branch-only deliverable.
  const deliveryPrimaryQty = deliveryQty
  const deliveryPrimaryLabel = formatEarliestDate((stockItem as any)?.earliest_delivery)
  // Phase 3: adapter-supplied delivery LATER qty. No fallback math.
  const deliveryLaterQty = (stockItem as any)?.delivery_dc_stock ?? 0
  const deliveryOverflowQty = Math.max((settledQuantity ?? 0) - deliveryPrimaryQty, 0)
  // Phase 2: trust the adapter. delivery_dc_date is set by the backend only
  // when a real DC date applies.
  const deliveryDcDate = isCheckingDcDate
    ? null
    : (dcDeliveryDate ?? (stockItem as any)?.delivery_dc_date ?? null)
  const deliveryDcDateLabel = formatEarliestDate(deliveryDcDate)
  const deliveryLaterLabel = deliveryDcDateLabel ?? '2-5 Business Days'
  const showDeliveryDcTier =
    canDeliver &&
    dcStock != null &&
    deliveryOverflowQty > 0 &&
    (deliveryDcDateLabel != null || isCheckingDcDate)
  const deliveryCapacity = Math.max(deliveryPrimaryQty, deliveryLaterQty)
  // Backorder per channel: qty above everything the channel can supply
  // (branch + depot) → one date. Dates: qty-matched probe for API-date
  // suppliers, else the (qty-independent) value from the stock row. A
  // backorder needs a date — none (e.g. Wolseley returned nothing) → blocked.
  const pickupCapacity = Math.max(pickupAvailableNow, pickupLaterQty)
  const useFreshBackorderDates =
    backorderDateFromApi &&
    freshBackorderDates != null &&
    freshBackorderDates.qty === (quantity ?? 0)
  const backorderCollectionDate = useFreshBackorderDates
    ? freshBackorderDates.collection
    : (stockItem?.backorder_collection_date ?? null)
  const backorderDeliveryDate = useFreshBackorderDates
    ? freshBackorderDates.delivery
    : (stockItem?.backorder_delivery_date ?? null)
  const isCheckingBackorderDate = isCheckingDcDate && needsBackorderDate
  // Tile-display qty: settled snapshot so numbers stay aligned with the
  // last-fetched DC date rather than the live keystroke.
  const requestedQty = settledQuantity ?? 0
  const isPickupBackorder =
    backorderAllowed &&
    canCollect &&
    requestedQty > pickupCapacity &&
    (backorderCollectionDate != null || isCheckingBackorderDate)
  const isDeliveryBackorder =
    backorderAllowed &&
    canDeliver &&
    requestedQty > deliveryCapacity &&
    (backorderDeliveryDate != null || isCheckingBackorderDate)
  const pickupBackorderLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability...'
    return (
      formatBackorderAvailability(backorderCollectionDate) ??
      'Collection date to be confirmed'
    )
  })()
  const deliveryBackorderLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability...'
    return (
      formatBackorderAvailability(backorderDeliveryDate) ??
      'Delivery date to be confirmed'
    )
  })()

  // Single-line availability summaries — mirror SupplierListingCard so pickup
  // and delivery each render one composed sentence ("45 available today · 200
  // from Fri 21 Aug"), rather than a header count plus separate rows.
  const pickupSummaryLabel = (() => {
    if (isPickupBackorder) return pickupBackorderLabel
    if (isCheckingDcDate && showPickupDcTier) return 'Checking availability...'
    const parts: string[] = []
    if (canCollect && pickupAvailableNow > 0) {
      const hasRealDate = pickupAvailableNowLabel != null
      parts.push(
        hasRealDate
          ? `${pickupAvailableNow.toLocaleString()} available ${pickupAvailableNowLabel}`
          : `${pickupAvailableNow.toLocaleString()} available · Collection date to be confirmed`,
      )
    }
    if (showPickupLaterTier && pickupLaterDisplayQty > 0) {
      parts.push(`${pickupLaterDisplayQty.toLocaleString()} from ${pickupLaterLabel}`)
    }
    return parts.length > 0 ? parts.join(' · ') : null
  })()

  const deliverySummaryLabel = (() => {
    if (isDeliveryBackorder) return deliveryBackorderLabel
    if (isCheckingDcDate && showDeliveryDcTier) return 'Checking availability...'
    if (!canDeliver) return null
    if (deliveryCapacity <= 0) return null
    if ((quantity ?? 0) <= deliveryPrimaryQty && deliveryPrimaryQty > 0) {
      const dateWord = deliveryPrimaryLabel ?? 'Later'
      return `${deliveryPrimaryQty.toLocaleString()} available to deliver from ${dateWord}`
    }
    const dcQty = Math.min((quantity ?? 0), deliveryCapacity)
    return `${dcQty.toLocaleString()} available to deliver from ${deliveryLaterLabel}`
  })()

  // Per-method capacity so the footer CTA gates on the SELECTED channel, not
  // on the union — a delivery-only backorder shouldn't be masked by pickup
  // stock the engineer isn't using. MAX (not SUM) mirrors master.
  const pickupMaxQty = canCollect ? Math.max(pickupAvailableNow, pickupLaterQty) : 0
  const deliveryMaxQty = canDeliver ? Math.max(deliveryPrimaryQty, deliveryLaterQty) : 0
  const methodMaxQty = selectedMethod === 'delivery' ? deliveryMaxQty : pickupMaxQty
  const backorderDateForMethod =
    selectedMethod === 'delivery' ? backorderDeliveryDate : backorderCollectionDate
  const canBackorderSelected =
    backorderAllowed &&
    (selectedMethod === 'delivery' ? canDeliver : canCollect) &&
    backorderDateForMethod != null
  const isOverStockSelected =
    quantity !== null &&
    quantity > methodMaxQty &&
    methodMaxQty > 0 &&
    !canBackorderSelected

  // Build fulfillment options with loading state support
  const fulfillmentOptions: FulfillmentOption[] = isLoadingStock ? [
    // While loading: show neutral state (no stock counts)
    {
      type: 'pickup',
      available: true,
      stockQuantity: 0,
      stockStatus: 'in_stock',
    },
    {
      type: 'delivery',
      available: true,
      stockQuantity: 0,
      stockStatus: 'in_stock',
    },
  ] : stockItem ? [
    // Stock loaded: show actual data. Pull through the earliest dates the
    // supplier exposed (CPS, Wolseley) — others leave them undefined and
    // the date line below renders nothing for that fulfillment type.
    {
      type: 'pickup',
      available: true,
      stockQuantity: collectionStock,
      stockStatus: collectionStock > 0 ? 'in_stock' : 'out_of_stock',
      earliestDate: (stockItem as any).earliest_collection,
    },
    {
      type: 'delivery',
      available: true,
      stockQuantity: deliveryStock,
      stockStatus: deliveryStock > 0 ? 'in_stock' : 'out_of_stock',
      earliestDate: (stockItem as any).earliest_delivery,
    },
  ] : isStockError ? [
    // API failed: show unavailable state (not "out of stock")
    {
      type: 'pickup',
      available: false,
      stockQuantity: 0,
      stockStatus: 'out_of_stock',
    },
    {
      type: 'delivery',
      available: false,
      stockQuantity: 0,
      stockStatus: 'out_of_stock',
    },
  ] : (product?.fulfillmentOptions || [])

  // Add to / update the cart. This is the single point where a branch change
  // is committed: for an existing line we update quantity and, when the
  // selected branch differs from the committed one, move the line in the same
  // call (backend blocks with a 409 if the product is already in the cart at
  // the target branch). For a new line we add at the selected/shown branch.
  // After updating an existing cart line, return the engineer to where they
  // came from (usually the cart they were editing). Falls back to the cart when
  // there's no in-app history (e.g. the page was deep-linked / hard-loaded).
  const goToPreviousPage = () => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate(`/${tenantSlug}/engineer/cart`)
    }
  }

  const handleAddToCart = async () => {
    const targetBranchId = selectedBranchId ?? displayBranchId
    if (!product || !targetBranchId || quantity === null) return

    if (existingCartItem?.id) {
      // Compare against the line's ACTUAL branch (not the URL), so the no-URL
      // same-branch case isn't treated as a move.
      const currentBranchId = String(existingCartItem.branchId)
      const branchChanged = String(targetBranchId) !== currentBranchId
      const quantityChanged = existingCartItem.quantity !== quantity
      // Method flips must be persisted too — otherwise a user who only swapped
      // Pickup ↔ Delivery on the tiles hits the "Already in cart" no-op path
      // and their choice never reaches the backend (mirrors master's
      // methodChanged handling in handleAddToCart).
      const methodChanged =
        existingCartItem.fulfillmentMethod !== selectedMethod

      // Already in the cart at this branch with the same quantity AND method —
      // nothing to persist, but still return to the previous page since Update
      // Cart means "done here".
      if (!branchChanged && !quantityChanged && !methodChanged) {
        addToast('Already in basket', 'info', 2000)
        goToPreviousPage()
        return
      }

      setIsUpdatingCart(true)
      // Optimistic sessionStorage stamp so a re-render before the PATCH lands
      // still reads the newly selected method. Rolled back on failure so the
      // rendered method stays consistent with what actually persisted.
      writeItemFulfillmentMethod(existingCartItem.id, selectedMethod)
      try {
        const updatedLine = await updateCartLine(existingCartItem.id, {
          branchId: branchChanged ? String(targetBranchId) : undefined,
          quantity,
          fulfillmentMethod: selectedMethod,
        })
        if (quantity > existingCartItem.quantity) {
          trackStandardProductAdd(updatedLine, quantity - existingCartItem.quantity)
        }
        if (branchChanged) {
          // Commit the branch to the URL so the committed branch tracks it.
          setSearchParams(
            prev => {
              prev.set('branchId', String(targetBranchId))
              return prev
            },
            { replace: true },
          )
        }
        addToast(branchChanged ? 'Moved to selected branch' : 'Basket updated', 'success', 2000)
        goToPreviousPage()
      } catch (error: any) {
        // Genuine failure (e.g. network). The viewed-branch-first lookup rules
        // out the "already in cart at target branch" conflict, so this is never
        // hit for the duplicate-branch case. Revert the preview on failure.
        if (branchChanged) setSelectedBranchId(currentBranchId)
        removeItemFulfillmentMethod(existingCartItem.id)
        addToast(error?.data?.error || 'Could not update basket', 'error', 3000)
      } finally {
        setIsUpdatingCart(false)
      }
      return
    }

    // New line — add at the selected/shown branch. Price not sent; backend
    // fetches from catalogue. Fulfillment method rides the create request so
    // the row is stored with the engineer's choice from the tiles.
    writeCartFulfillmentMethod(product.supplierId, String(targetBranchId), selectedMethod)
    addToCartMutation({
      product_id: Number(product.productId),
      branch_id: String(targetBranchId),
      quantity,
      fulfillment_method: selectedMethod,
      // Analytics only — stripped before the request. Either channel having
      // stock counts as available, matching what the page shows the engineer.
      inStockAtBranch: collectionStock > 0 || deliveryStock > 0,
      productAddMode: { mode: PRODUCT_ADD_MODES.standard, productPosition: null },
    }, {
      onSuccess: (createdItem: any) => {
        if (createdItem?.id) {
          writeItemFulfillmentMethod(createdItem.id, selectedMethod)
        }
        setQuantity(orderMultiple)
        addToast(`Added "${product.productName}" to basket`, 'success', 2000)
      },
      onError: (error) => {
        addToast(
          `Failed to add to basket: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'error',
          3000,
        )
      },
    })
  }

  const hasSupportedFulfillment = canCollect || canDeliver
  // Check if no branch is available (no branchId in URL and no nearby branches with stock).
  // Uses showLoadingSkeleton so a background cart-stock refetch after a branch
  // swap doesn't briefly resurrect this state.
  const noBranchAvailable = !displayBranchId && !showLoadingSkeleton

  // Auto-flip to delivery when pickup isn't supported (and vice versa). Runs
  // once stock resolves so the initial 'pickup' seed doesn't strand the user
  // on a disabled tile for a delivery-only branch.
  useEffect(() => {
    if (showLoadingSkeleton || stockUnavailable) return
    if (selectedMethod === 'pickup' && !canCollect && canDeliver) {
      setSelectedMethod('delivery')
    } else if (selectedMethod === 'delivery' && !canDeliver && canCollect) {
      setSelectedMethod('pickup')
    }
  }, [canCollect, canDeliver, showLoadingSkeleton, stockUnavailable, selectedMethod])

  // Derived footer state — matches master ProductDetail's shape so the CTA
  // label logic (Checking / Only N / Sold in multiples / Add N for pickup) is
  // identical across master and raw product pages.
  const canAdd =
    !showLoadingSkeleton &&
    !isCheckingDcDate &&
    !stockUnavailable &&
    !noBranchAvailable &&
    hasSupportedFulfillment &&
    !isOverStockSelected &&
    !isInvalidMultiple &&
    quantity !== null &&
    quantity > 0 &&
    (methodMaxQty > 0 || canBackorderSelected) &&
    // Guard rapid double-taps: the local isUpdatingCart wrapper only covers
    // the update-line flow, so the add flow was unguarded and could fire
    // multiple POSTs (+ duplicate analytics events) on rapid taps. Mirrors
    // master ProductDetail, which gates on both isAdding and isUpdating
    // from useCart().
    !isUpdatingCart &&
    !isAdding &&
    !isUpdating

  const footerButtonLabel = (() => {
    if (showLoadingSkeleton) return 'Checking stock…'
    if (isCheckingDcDate) return 'Checking availability…'
    if (noBranchAvailable) return 'No branch in range'
    if (stockUnavailable) return 'Out of stock'
    if (methodMaxQty <= 0 && !canBackorderSelected) {
      return selectedMethod === 'delivery'
        ? 'Not available for delivery'
        : 'Not available for collection'
    }
    if (isOverStockSelected) return `Only ${methodMaxQty.toLocaleString()} available`
    if (isInvalidMultiple) return `Sold in multiples of ${orderMultiple}`
    if (quantity === null || quantity <= 0) return 'Enter a quantity'
    const verb = existingCartItem ? 'Update' : 'Add'
    return `${verb} ${quantity} for ${selectedMethod === 'delivery' ? 'delivery' : 'collection'}`
  })()

  const footerSummary = (() => {
    if (!product) return null
    const methodLabel = selectedMethod === 'delivery' ? 'Delivery' : 'Collection'
    // Tier-aware effective date (mirrors master's SupplierListingCard):
    //   backorder → backorder date for the channel
    //   qty exceeds primary tier → DC date (fall back to primary if adapter
    //                              didn't supply one)
    //   qty within primary → primary date
    // Without the tier check, the footer would advertise a later DC date
    // whenever the supplier surfaced one — even for a qty the branch shelf
    // already covers — AND would ignore the backorder date entirely, both of
    // which contradict what the tile above is showing.
    const primaryPickupDate = (stockItem as any)?.earliest_collection ?? null
    const primaryDeliveryDate = (stockItem as any)?.earliest_delivery ?? null
    const effectivePickupDate = isPickupBackorder
      ? backorderCollectionDate
      : requestedQty > pickupAvailableNow
        ? (pickupDcDate ?? primaryPickupDate)
        : primaryPickupDate
    const effectiveDeliveryDate = isDeliveryBackorder
      ? backorderDeliveryDate
      : deliveryOverflowQty > 0
        ? (deliveryDcDate ?? primaryDeliveryDate)
        : primaryDeliveryDate
    const effectiveDate =
      selectedMethod === 'delivery' ? effectiveDeliveryDate : effectivePickupDate
    const dateLabel =
      formatEarliestDate(effectiveDate) ??
      (selectedMethod === 'delivery' ? 'TBC' : 'Collection date to be confirmed')
    const total = perUnitPrice(displayPrice, orderMultiple) * (quantity ?? 0)
    return { methodLabel, dateLabel, total }
  })()

  if (!session) {
    return null
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. Shared EngineerHeader
       * suppressed: only the back+title bar renders. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigate(-1)
              }}
              className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
              aria-label="Go back"
              type="button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-nav font-bold truncate text-sm flex-1">
              {product?.productName || 'Loading...'}
            </h1>
            {/* Favourite toggle. This page shows one supplier's SKU, so
             * the heart favourites the RAW product (linked-product
             * favourite). Tapping it later from the Home tab reopens
             * this exact supplier product. See DEV-46921 §7 clarification
             * — favourites can target either master or raw. Hidden until
             * we have a productId so we never favourite `null`. Gated
             * by the HOME_TAB_ENABLED build flag so environments with
             * the Home tab hidden don't show a heart leading nowhere. */}
            {HOME_TAB_ENABLED && productId && (() => {
              const target = { type: 'raw' as const, id: productId }
              const active = isFavourited(target)
              return (
                <button
                  type="button"
                  onClick={() =>
                    toggleFavourite({ target, productName: product?.productName || 'Unnamed product' })
                  }
                  // The ::before extends the tap area to 44px without changing the look.
                  className="relative p-2 -mr-2 text-nav hover:bg-nav-hover rounded-xl transition-colors before:absolute before:-inset-1 before:content-['']"
                  aria-label={active ? 'Remove from favourites' : 'Add to favourites'}
                  aria-pressed={active}
                >
                  <Heart
                    className="w-5 h-5"
                    fill={active ? 'currentColor' : 'none'}
                  />
                </button>
              )
            })()}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto pb-24">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-3" />
            <p className="text-gray-500 text-sm">Loading product details...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-gray-900 text-lg font-bold mb-2">Failed to load product</h2>
            <p className="text-gray-500 text-sm mb-4 text-center">
              We couldn't load the product details. Please try again.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Go back
              </Button>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* Not Found State - API returned null */}
        {!isLoading && !error && product === null && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-gray-900 text-lg font-bold mb-2">Product not found</h2>
            <p className="text-gray-500 text-sm mb-4">
              The product you're looking for doesn't exist or has been removed.
            </p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        )}

        {/* Product Content */}
        {product && (
          <>
            {/* Product Image Carousel - Swipeable on mobile */}
            <div className="px-3 sm:px-4 pt-4">
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div
              className="bg-gray-100 h-56 sm:h-72 w-full relative overflow-hidden"
              onTouchStart={(e) => {
                if (!product.images || product.images.length <= 1) return
                const touch = e.touches[0]
                const startX = touch.clientX

                const handleTouchMove = (moveEvent: TouchEvent) => {
                  const currentX = moveEvent.touches[0].clientX
                  const diff = startX - currentX

                  if (Math.abs(diff) > 50) {
                    if (diff > 0) {
                      // Swipe left - next image
                      setCurrentImageIndex(prev =>
                        prev === product.images!.length - 1 ? 0 : prev + 1
                      )
                    } else {
                      // Swipe right - previous image
                      setCurrentImageIndex(prev =>
                        prev === 0 ? product.images!.length - 1 : prev - 1
                      )
                    }
                    cleanup()
                  }
                }

                const handleTouchEnd = () => {
                  cleanup()
                }

                const cleanup = () => {
                  document.removeEventListener('touchmove', handleTouchMove)
                  document.removeEventListener('touchend', handleTouchEnd)
                }

                document.addEventListener('touchmove', handleTouchMove)
                document.addEventListener('touchend', handleTouchEnd)
              }}
            >
              {product.images && product.images.length > 0 ? (
                <>
                  {/* Images */}
                  <div
                    className="flex h-full transition-transform duration-300 ease-out"
                    style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                  >
                    {product.images.map((image: any, index: number) => (
                      <div key={image.id} className="w-full h-full flex-shrink-0 relative">
                        <img
                          src={image.url}
                          alt={`${product.productName} - Image ${index + 1}`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            // eslint-disable-next-line no-console
                            console.error('[IMG_ERROR] Product carousel image failed to load:', image.url)
                            e.currentTarget.style.display = 'none'
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement
                            if (fallback) fallback.style.display = 'flex'
                          }}
                        />
                        <div className="flex-col items-center justify-center w-full h-full bg-gray-50 absolute inset-0" style={{ display: 'none' }}>
                          <Package className="w-16 h-16 text-gray-300 mb-2" />
                          <p className="text-gray-400 text-sm">No image available</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Navigation arrows (desktop) */}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(prev =>
                          prev === 0 ? product.images!.length - 1 : prev - 1
                        )}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hidden sm:block z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-700" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(prev =>
                          prev === product.images!.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hidden sm:block z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Dots indicator */}
                  {product.images.length > 1 && (
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center z-10">
                      <div className="bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full flex gap-1.5">
                        {product.images.map((_: any, index: number) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={clsx(
                              'h-2 rounded-full transition-all',
                              index === currentImageIndex
                                ? 'bg-white w-6'
                                : 'bg-white/60 w-2'
                            )}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.productName}
                  className="w-full h-full object-contain p-4"
                  onError={(e) => {
                    // eslint-disable-next-line no-console
                    console.error('[IMG_ERROR] Product imageUrl fallback failed to load:', product.imageUrl)
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                  <Package className="w-16 h-16 text-gray-300 mb-2" />
                  <p className="text-gray-400 text-sm">No image available</p>
                </div>
              )}
            </div>
                <div className="p-3 sm:p-4 space-y-2">
              {/* Badges */}
              {/* DEV-51188: show the same In Stock / Backorder summary pill
               * the engineer sees on the search card and cart line for the
               * same product. Uses pickupCapacity/deliveryCapacity (branch +
               * DC combined) so a "delivery from DC" supplier reads as IN
               * STOCK even when branch_stock=0. IN STOCK wins over BACKORDER
               * whenever any viable channel has capacity — matches how
               * search flags the master product. Suppressed while stock is
               * loading or unavailable (existing skeleton / "Not available"
               * tile already speaks for those states). */}
              {(() => {
                // Per-channel classify mirrors getCartItemStockView in
                // utils/cartItemStock.ts: qty ≤ capacity → in-stock;
                // qty > capacity + supplier backorder date → backorder; else
                // no pill (the "Not available" tile / red exceeds warning
                // speaks). Uses requestedQty (settled) so the pill agrees
                // with the tile numbers; branch/qty changes re-drive it.
                const stockPillReady = !isLoadingStock && !stockUnavailable
                const pickupInStock =
                  canCollect && requestedQty > 0 && requestedQty <= pickupCapacity
                const deliveryInStock =
                  canDeliver && requestedQty > 0 && requestedQty <= deliveryCapacity
                const showInStockPill = stockPillReady && (pickupInStock || deliveryInStock)
                // Suppress BACKORDER while the qty-matched backorder probe
                // is in flight for API-date suppliers — isPickupBackorder /
                // isDeliveryBackorder are truthy during the probe (before a
                // real date arrives) so the tile can show "Checking
                // availability...", but the summary pill would otherwise
                // flash BACKORDER then disappear if the probe returns no
                // date. Wait for the confirmed date.
                const showBackorderPill =
                  stockPillReady &&
                  !showInStockPill &&
                  !isCheckingBackorderDate &&
                  (isPickupBackorder || isDeliveryBackorder)
                const showAnyBadge =
                  product.isVanStock ||
                  product.isPreferred ||
                  product.isRestricted ||
                  showBackorderPill ||
                  showInStockPill
                if (!showAnyBadge) return null
                return (
                  <div className="flex flex-wrap gap-1.5">
                    {product.isVanStock && <VanStockBadge size="xs" />}
                    {showInStockPill && <InStockBadge />}
                    {showBackorderPill && <BackorderBadge variant="amber-plain" />}
                    {product.isPreferred && (
                      <span className="inline-flex items-center gap-1 text-xs bg-emerald-500 text-white px-2.5 py-1 rounded-md font-bold uppercase">
                        <Star className="w-3 h-3" fill="currentColor" />
                        PREFERRED
                      </span>
                    )}
                    {product.isRestricted && (
                      <span className="inline-flex items-center text-xs bg-red-600 text-white px-2.5 py-1 rounded-md font-bold uppercase">
                        RESTRICTED
                      </span>
                    )}
                  </div>
                )
              })()}

              {/* Product Info */}
              <div>
                {/* Title + price (inline on wider phones, like the master page) */}
                <div className="flex flex-col min-[400px]:flex-row min-[400px]:items-start min-[400px]:justify-between gap-0.5 min-[400px]:gap-3">
                  <h2 className="text-gray-900 text-lg font-bold">
                    {product.productName}
                  </h2>
                  <div className="text-brand-primary font-bold flex-shrink-0 whitespace-nowrap">
                    {priceUnavailable ? (
                      <span className="text-gray-400">
                        £-
                        <span className="text-xs font-normal ml-1">
                          / {displayUnitOfMeasure || '-'}
                        </span>
                      </span>
                    ) : (
                      <>
                        £{displayPrice.toFixed(2)}
                        <span className="text-gray-400 text-xs font-normal ml-1">
                          / {orderMultiple > 1 ? `${displayUnitOfMeasure || 'Each'} of ${orderMultiple}` : (displayUnitOfMeasure || 'Each')}
                        </span>
                        {orderMultiple > 1 && (
                          <span className="block text-gray-400 text-xs font-normal">
                            £{perUnitPrice(displayPrice, orderMultiple).toFixed(3)} per unit
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {priceUnavailable && (
                  <p className="text-amber-600 text-xs mt-1">Price unavailable</p>
                )}
                <div className="space-y-1 mt-2">
                  <p className="text-gray-500 text-sm">
                    <span className="font-medium text-gray-700">SKU:</span> {product.supplierProductCode}
                  </p>
                  {product.manufacturerCode && (
                    <p className="text-gray-500 text-sm">
                      <span className="font-medium text-gray-700">Product code:</span> {product.manufacturerCode}
                    </p>
                  )}
                </div>

                {/* Product info accordion — matches master detail. Brand
                    (DEV-51596) lives inside the accordion with a dash
                    fallback (AC2); the accordion is shown even when the
                    product has no text description so the brand row always
                    has a home. */}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowDescription(prev => !prev)}
                    className={clsx(
                      'w-full flex items-center justify-between px-3 py-2 bg-gray-50 transition-colors',
                      showDescription ? 'rounded-t-md' : 'rounded-md'
                    )}
                  >
                    <span className="text-xs font-medium text-gray-500">Product info</span>
                    <ChevronDown className={clsx(
                      'w-3.5 h-3.5 text-gray-400 transition-transform duration-200',
                      showDescription && 'rotate-180'
                    )} />
                  </button>
                  <div className={clsx(
                    'grid transition-all duration-200 ease-in-out',
                    showDescription ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}>
                    <div className="overflow-hidden">
                      <div className="px-3 py-2.5 bg-gray-50 rounded-b-md space-y-1.5">
                        <p className="text-gray-600 text-xs leading-relaxed">
                          <span className="font-medium text-gray-700">Brand:</span>{' '}
                          {product.brand || '-'}
                        </p>
                        {product.productDescription && (
                          <p className="text-gray-600 text-xs leading-relaxed">
                            {product.productDescription}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quantity selector — moved out of the sticky footer so the
                 * engineer commits the qty before picking a fulfillment method,
                 * matching the master product page. Full-width stepper reads as
                 * part of the product-info card. Over-stock / invalid-multiple
                 * warnings surface on the sticky footer CTA below. */}
                <div className="mt-3">
                  <h3 className="text-gray-800 font-bold text-xs mb-2">Choose your quantity</h3>
                  <QuantitySelector
                    value={quantity}
                    onChange={(next) =>
                      setQuantity(next ?? Math.max(orderMultiple, 1))
                    }
                    multiple={orderMultiple}
                    size="md"
                    fullWidth
                    disabled={showLoadingSkeleton || stockUnavailable || noBranchAvailable}
                  />
                </div>
              </div>
                </div>
              </div>
            </div>

            <div className="px-3 sm:px-4 py-4 space-y-4">
              {/* Availability card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-900 font-semibold text-sm">
                    Availability
                  </h3>
                </div>
                {showLoadingSkeleton ? (
                  // Keep the tile shell visible during a branch-swap refetch so
                  // the section doesn't collapse into a single spinner banner
                  // (mirrors master SupplierListingCard's isCheckingStock state).
                  <div className="space-y-[10px]">
                    {(['pickup', 'delivery'] as const).map(type => (
                      <div
                        key={type}
                        className="rounded-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="px-3 py-2.5">
                          <div className="flex items-start gap-2">
                            <span
                              className="mt-0.5 h-4 w-4 flex-none rounded-full border-2 border-gray-200"
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1 animate-pulse">
                              <div className="flex items-center gap-1.5 text-gray-400">
                                {type === 'pickup'
                                  ? <Package className="w-3.5 h-3.5 flex-shrink-0" />
                                  : <Truck className="w-3.5 h-3.5 flex-shrink-0" />}
                                <span className="text-[11px] font-semibold">
                                  {type === 'pickup' ? 'Collection' : 'Delivery'}
                                </span>
                              </div>
                              <div className="mt-2 h-3.5 rounded bg-gray-200 w-40 max-w-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : stockUnavailable ? (
                  // Same shell, but each tile carries the "Not available" copy
                  // instead of a skeleton, so the layout doesn't reflow.
                  <div className="space-y-[10px]">
                    {(['pickup', 'delivery'] as const).map(type => (
                      <div
                        key={type}
                        className="rounded-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="px-3 py-2.5">
                          <div className="flex items-start gap-2">
                            <span
                              className="mt-0.5 h-4 w-4 flex-none rounded-full border-2 border-gray-200"
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 text-gray-400">
                                {type === 'pickup'
                                  ? <Package className="w-3.5 h-3.5 flex-shrink-0" />
                                  : <Truck className="w-3.5 h-3.5 flex-shrink-0" />}
                                <span className="text-[11px] font-semibold">
                                  {type === 'pickup' ? 'Collection' : 'Delivery'}
                                </span>
                              </div>
                              <p className="mt-1.5 text-xs text-amber-600">
                                Out of stock
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : fulfillmentOptions && fulfillmentOptions.length > 0 ? (
                  <div className={clsx('space-y-[10px]', isCheckingDcDate && 'animate-pulse')}>
                    {/* Pickup — radio-select tile (mirrors master's SupplierListingCard) */}
                    <div
                      role="button"
                      tabIndex={canCollect ? 0 : -1}
                      aria-pressed={selectedMethod === 'pickup'}
                      aria-disabled={!canCollect}
                      onClick={() => canCollect && setSelectedMethod('pickup')}
                      onKeyDown={(e) => {
                        if (!canCollect) return
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedMethod('pickup')
                        }
                      }}
                      className={clsx(
                        'rounded-xl overflow-hidden text-left transition-colors border',
                        !canCollect && 'cursor-not-allowed',
                        canCollect && 'cursor-pointer',
                        selectedMethod === 'pickup'
                          ? 'border-brand-primary'
                          : 'border-gray-200 hover:border-gray-300',
                      )}
                    >
                      <div className="px-3 py-2.5 border-b border-gray-100">
                        <div className="flex items-start gap-2">
                          <span
                            className={clsx(
                              'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                              selectedMethod === 'pickup'
                                ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                                : 'border-gray-300',
                            )}
                            aria-hidden="true"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <Package className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="text-[11px] font-semibold">Collection</span>
                            </div>
                            {pickupSummaryLabel ? (
                              <p className={clsx(
                                'mt-1.5 text-sm font-semibold leading-snug',
                                isCheckingDcDate && showPickupDcTier
                                  ? 'italic text-gray-400'
                                  : 'text-gray-800',
                              )}>
                                {pickupSummaryLabel}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-xs text-gray-500">Not available for collection.</p>
                            )}
                          </div>
                        </div>
                      </div>
                      {displayBranchName && (
                        <div className="px-3 py-2 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                              <MapPin className="w-3.5 h-3.5 flex-none text-gray-500" />
                              <span className="truncate">
                                {displayBranchName}
                                {(stockItem as any)?.distance_miles != null && (
                                  <> · {((stockItem as any).distance_miles as number).toFixed(1)} mi</>
                                )}
                              </span>
                            </p>
                          </div>
                          {hasOtherBranches && (
                            <button
                              type="button"
                              onClick={(e) => {
                                // Prevent the outer tile's radio-select from firing.
                                e.stopPropagation()
                                setIsBranchSelectorOpen(prev => !prev)
                              }}
                              className="flex-none px-1 text-xs font-bold text-brand-primary hover:underline"
                            >
                              Change
                            </button>
                          )}
                        </div>
                      )}
                      {/* Branch selector — nested inside the Pickup tile so the
                          picker visually belongs to Collection (delivery is
                          branch-agnostic). Row taps stop propagation so the
                          outer tile's radio-select doesn't re-fire. */}
                      {isBranchSelectorOpen && hasOtherBranches && (
                        <div className="mx-2 mb-2 mt-1 rounded-lg border border-gray-200 overflow-hidden">
                          {branchOptions.map((b: {
                            branchId: string
                            branchName: string
                            distanceMiles: number | null
                            pickupStock: number
                          }) => {
                            const isBranchSelected =
                              String(b.branchId) === String(selectedBranchId)
                            return (
                              <button
                                key={b.branchId}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleSelectBranch(b.branchId)
                                }}
                                className={clsx(
                                  'flex w-full items-center justify-between gap-3 border-t border-gray-100 px-2.5 py-1.5 text-left transition-colors first:border-t-0',
                                  isBranchSelected
                                    ? 'bg-gray-50'
                                    : 'bg-white hover:bg-gray-50',
                                )}
                              >
                                <span className="min-w-0">
                                  <span className="text-xs text-gray-900">
                                    {b.branchName}
                                  </span>
                                  {b.distanceMiles != null && (
                                    <span className="mt-0.5 block text-[10px] text-gray-600">
                                      {b.distanceMiles.toFixed(1)} mi
                                    </span>
                                  )}
                                </span>
                                <span
                                  className={clsx(
                                    'max-w-32 text-right text-[10px] font-semibold',
                                    b.pickupStock > 0
                                      ? 'text-gray-700'
                                      : 'text-red-500',
                                  )}
                                >
                                  {b.pickupStock > 0
                                    ? b.pickupStock.toLocaleString()
                                    : 'Out of stock'}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Delivery — radio-select tile */}
                    <div
                      role="button"
                      tabIndex={canDeliver ? 0 : -1}
                      aria-pressed={selectedMethod === 'delivery'}
                      aria-disabled={!canDeliver}
                      onClick={() => canDeliver && setSelectedMethod('delivery')}
                      onKeyDown={(e) => {
                        if (!canDeliver) return
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedMethod('delivery')
                        }
                      }}
                      className={clsx(
                        'rounded-xl overflow-hidden text-left transition-colors border',
                        !canDeliver && 'cursor-not-allowed',
                        canDeliver && 'cursor-pointer',
                        selectedMethod === 'delivery'
                          ? 'border-brand-primary'
                          : 'border-gray-200 hover:border-gray-300',
                      )}
                    >
                      <div className="px-3 py-2.5">
                        <div className="flex items-start gap-2">
                          <span
                            className={clsx(
                              'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                              selectedMethod === 'delivery'
                                ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                                : 'border-gray-300',
                            )}
                            aria-hidden="true"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              <Truck className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="text-[11px] font-semibold">Delivery</span>
                            </div>
                            {deliverySummaryLabel ? (
                              <p className={clsx(
                                'mt-1.5 text-sm font-semibold leading-snug',
                                isCheckingDcDate && showDeliveryDcTier
                                  ? 'italic text-gray-400'
                                  : 'text-gray-800',
                              )}>
                                {deliverySummaryLabel}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-xs text-gray-500">Not available for delivery.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

            </div>
          </>
        )}
      </main>

      {/* Sticky action footer — matches master product page: summary strip
       * (supplier · branch · total) on top, full-width rounded CTA in the
       * middle, method · date fine-print on the bottom. The quantity stepper
       * lives on the product card above, so the footer is CTA-only. */}
      {product && (
        <div
          ref={toastClearanceRef}
          className="fixed left-0 right-0 z-30 border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(32,26,46,0.14)]"
          style={{ bottom: '4rem' }}
        >
          <div className="max-w-3xl mx-auto px-4 pt-3 pb-3">
            {footerSummary && (
              <div className="mb-2 flex min-w-0 items-center justify-center text-xs leading-tight text-gray-600">
                <span className="shrink-0 font-bold text-gray-900">
                  {product.supplierName}
                </span>
                {displayBranchName && (
                  <span className="min-w-0 truncate">
                    {' · '}
                    {displayBranchName}
                  </span>
                )}
                <span className="shrink-0">
                  {' · Total '}
                  <span className="font-bold text-brand-primary">
                    £{footerSummary.total.toFixed(2)}
                  </span>
                </span>
              </div>
            )}

            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!canAdd}
              className="min-h-10 w-full rounded-full px-3 text-xs font-bold bg-brand-primary hover:bg-brand-primary-hover active:bg-brand-primary-dark disabled:opacity-50 text-[var(--brand-primary-text)] transition-colors"
            >
              {footerButtonLabel}
            </button>

            {footerSummary && (
              <div className="mt-2 min-w-0 truncate text-center text-xs leading-tight text-gray-600">
                {footerSummary.methodLabel}
                {' · '}
                {footerSummary.dateLabel}
              </div>
            )}
          </div>
        </div>
      )}
    </EngineerLayout>
  )
}

